import React, { FC, useEffect, useMemo, useState } from "react";
import { PaginateModel } from "../../../models/PaginateModel";
import { PaginationLength } from "../../../../_metronic/partials/pagination/PaginationLength";
import { Paginate } from "../../../../_metronic/partials/pagination/Paginate";
import { DEFAULT_PER_PAGE } from "../../../constants/common";
import ConfigModal from "../../../components/Modal/ConfigModal";
import { HeaderModel, ModalModel } from "../../../models/CommonModel";
import CashBookAccountTable from "../components/CashBookAccountTable";
import * as XLSX from 'xlsx';
import { Confirm, download, Toast } from "../../../modules/common/helper";
import { exportPayments, forceRemovePayment, getPayments, importPayments, removePayments, restorePayments } from "../AccountBooksCRUD";
import { PaymentRowModel, PaymentRowSearchModel } from "../../../models/account-books/PaymentAccount";
import { useIntl } from "react-intl";
import ExcelToObjectImportModal from "../../../components/Modal/ExcelToObjectImportModal";
import { Link } from "react-router-dom";
import { toAbsoluteUrl } from "../../../../_metronic/helpers";
import CashBookAccountDeletedTable from "../components/CashBookAccountDeletedTable";

interface Props {
    opening?: string
    closing?: string
    changeTab: (tab: string) => void
    isFullYear: boolean
}

const CashBookAccount: FC<Props> = ({opening, closing, changeTab, isFullYear}) => {
    const intl = useIntl();
    const method = 'cash';
    const methodLabel = 'Tiền mặt';
    const initSearch = useMemo(() => ({
        page: 1,
        per_page: DEFAULT_PER_PAGE,
        start_date: opening,
        end_date: closing,
        method: method,
    }), [opening, closing])

    const [search, setSearch] = useState<PaymentRowSearchModel>(initSearch)
    const [data, setData] = useState<PaginateModel<PaymentRowModel>>()
    const [subTotal, setSubTotal] = useState<number>(0)
    const [trashedCount, setTrashedCount] = useState<number>(0)
    const [selected, setSelected] = useState<number[]>([]);
    const [configModal, setConfigModal] = useState<ModalModel>({
        show: false,
        mode: 'add',
    });
    const [hideCols, setHideCols] = useState<string[]>([]);
    const header: HeaderModel[] = [
        {
            label: 'Ngày ghi sổ',
            field: 'posting_date',
            style: { maxWidth: "100px", minWidth: "100px", },
            sortable: true
        },
        {
            label: 'Số hiệu',
            field: 'voucher_no',
            style: { maxWidth: "100px", minWidth: "100px", },
            sortable: true
        },
        {
            label: 'Thu',
            field: 'in_amount',
            style: { maxWidth: "120px", minWidth: "120px", textAlign: "right" },
            sortable: true
        },
        {
            label: 'Chi',
            field: 'out_amount',
            style: { maxWidth: "120px", minWidth: "120px", textAlign: "right" },
            sortable: true
        },
        {
            label: 'Diễn giải',
            field: 'description',
            style: { maxWidth: "200px", minWidth: "200px", textAlign: "center"},
            sortable: false
        }
    ]

    const handleChangeVisCols = (e: any) => {
        let hiddenCols = [...hideCols];
        if (!e.target?.checked) {
            setHideCols([...hiddenCols, e.target.name])
        } else {
            setHideCols(hiddenCols.filter((item) => item !== e.target.name))
        }
    }

    useEffect(() => {
        if (opening && closing) {
            getData({...initSearch});
        }
    }, [opening, closing, initSearch])

    const getData = async (searchParams: PaymentRowSearchModel) => {
        try {
            const { data: resData } = await getPayments(searchParams);
            setData(resData.paginate_data);
            setSubTotal(resData.sub_total);
            setTrashedCount(resData.trashed_count ?? 0);
            setSearch(searchParams)
        } catch (e: any) {
            Toast.fire({
                icon: 'error',
                title: 'Đã có lỗi xảy ra, vui lòng thử lại sau!'
            })
        }
    }
    const handleSearch = () => {

    }

    const handlePageLengthChange = (e: any) => {
        getData({ ...search, page: 1, per_page: +e.target.value })
    }

    const handlePageChange = (data: any) => {
        getData({ ...search, page: data.selected + 1 })
    }

    const handleSort = (sortBy: string, direction: 'asc' | 'desc' | 'none') => {
        const currentSearch = { ...search };
        const sortParams: PaymentRowSearchModel = { ...currentSearch, sort_by: sortBy, sort_direction: direction };
        if (direction === 'none') {
            delete sortParams.sort_by;
            delete sortParams.sort_direction;
        }
        getData({ ...sortParams });
    }

    const handleExport = async () => {
        const { data } = await exportPayments({ ...search });
        download(data, '5-So Quy TM');
    }

    const [showImport, setShowImport] = useState<boolean>(false);
    
    interface ExcelRow {
        [key: string]: any;
    }

    const handleImport = async (paymentsData: ExcelRow[]) => {
        try {
            // ensure every row has a "Phương thức" column with the fixed value
            const rowsWithMethod = paymentsData.map(r => {
                if (r.hasOwnProperty('Phương thức')) {
                    return r;
                }
                return { ...r, 'Phương thức': methodLabel };
            });

            // convert the array of objects back into an Excel file blob
            const ws = XLSX.utils.json_to_sheet(rowsWithMethod);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: 'application/octet-stream' });
            const file = new File([blob], 'payments.xlsx', {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });

            const formData = new FormData();
            formData.append('file', file);

            const { data } = await importPayments(formData);
            getData({...initSearch});
            Toast.fire({
                icon: 'success',
                title: data?.message || intl.formatMessage({ id: 'COMMON.SUCCESS_NOTY' }),
            })
        } catch (error: any) {
            Toast.fire({
            icon: 'error',
            title: error?.response?.data?.message || intl.formatMessage({ id: 'COMMON.ERROR_NOTY' }),
            })
        }
    }

    const handleDeleteSelected = () => {
        Confirm.fire({
            icon: 'question',
            title: intl.formatMessage({ id: 'COMMON.CONFIRM_DELETE' }, { name: 'các dữ liệu đã chọn' }),
            confirmButtonText: intl.formatMessage({ id: 'COMMON.YES' }),
            cancelButtonText: intl.formatMessage({ id: 'COMMON.NO' }),
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    const { data } = await removePayments(selected);
                    Toast.fire({
                    icon: 'success',
                    title: data?.message || intl.formatMessage({ id: 'COMMON.SUCCESS_NOTY' }),
                    });
                    setSelected([])
                    getData({ ...search, page: 1 });
                } catch (error: any) {
                    Toast.fire({
                    icon: 'error',
                    title: error?.response?.data?.message,
                    })
                }
            }
        })
    }

    const handleRestore = () => {
        Confirm.fire({
            icon: 'question',
            title: 'Xác nhận khôi phục dữ liệu đã chọn',
            confirmButtonText: intl.formatMessage({ id: 'COMMON.YES' }),
            cancelButtonText: intl.formatMessage({ id: 'COMMON.NO' }),
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    const { data } = await restorePayments(selected);
                    Toast.fire({
                    icon: 'success',
                    title: data?.message || intl.formatMessage({ id: 'COMMON.SUCCESS_NOTY' }),
                    });
                    setSelected([])
                    getData({ ...search, page: 1 });
                } catch (error: any) {
                    Toast.fire({
                    icon: 'error',
                    title: error?.response?.data?.message,
                    })
                }
            }
        })
    }

    const handleForceDelete = () => {
        Confirm.fire({
            icon: 'question',
            title: 'Xác nhận xóa vĩnh viễn dữ liệu đã chọn',
            confirmButtonText: intl.formatMessage({ id: 'COMMON.YES' }),
            cancelButtonText: intl.formatMessage({ id: 'COMMON.NO' }),
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    const { data } = await forceRemovePayment(selected);
                    Toast.fire({
                    icon: 'success',
                    title: data?.message || intl.formatMessage({ id: 'COMMON.SUCCESS_NOTY' }),
                    });
                    setSelected([])
                    getData({ ...search, page: 1 });
                } catch (error: any) {
                    Toast.fire({
                    icon: 'error',
                    title: error?.response?.data?.message,
                    })
                }
            }
        })
    }

    const toggleIncludeTrashed = (e: React.ChangeEvent<HTMLInputElement>) => {
        getData({ 
            ...search, 
            include_trashed: e.target.checked ? 1 : 0, 
            page: 1 
        })
    }

    const showTrashedOnly = (isTrashOnly: boolean = false) => {
        getData({ 
            ...search, 
            trashed_only: isTrashOnly ? 1 : 0, 
            page: 1 
        })
    }

    return <>
    <div className='card mt-3'>
      <div className="card-header card-header-stretch">
        <h3 className="card-title">
            <button type="button" className="btn btn-primary btn-sm mx-1 mb-1 me-2" onClick={() => changeTab('index')}>
                <i className="fas fa-angle-left"/> 
            </button>
            <span
                className={'cursor-pointer ' + (search?.trashed_only === 1 ? "text-muted" : '')}
                onClick={() => showTrashedOnly(false)}
            >Sổ quỹ TM</span> <span className="mx-2">|</span> <span 
                className={'cursor-pointer ' + (search?.trashed_only !== 1 ? "text-muted" : '')}
                onClick={() => showTrashedOnly(true)}
            >
                    <i className="fa fa-trash-alt"></i> Thùng rác <span className="bg-danger count-bagde text-white">{trashedCount < 100 ? trashedCount : '99+'}</span>
            </span>
        </h3>
        <div className="card-toolbar align-items-center col-12 col-md-auto d-block d-md-flex">
            <div className="btns-group">
                {search?.trashed_only === 1 ? (
                    <>  {selected?.length > 0 && (
                            <>
                                <button type="button" className="col-auto btn btn-sm btn-success mt-0 mx-1 mb-2" onClick={() => handleRestore()}>
                                    <i className="fas fa-undo"></i> Khôi phục 
                                </button>
                                <button type="button" className="col-auto btn btn-sm btn-danger mt-0 mx-1 mb-2" onClick={() => handleForceDelete()}>
                                    <i className="fa fa-trash-alt"></i> Xóa vĩnh viễn
                                </button>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        {selected?.length > 0 && (
                            <button type="button" className="col-auto btn btn-sm btn-danger mt-0 mx-1 mb-2" onClick={() => handleDeleteSelected()}>
                                <i className="bi bi bi-trash"></i> {intl.formatMessage({ id: 'COMMON.DELETE' }, { name: 'selected' })} 
                            </button>
                        )}
                        {/* <Link className="col-auto btn btn-sm btn-primary mt-0 mx-1 mb-2" target="__blank" to={toAbsoluteUrl('/media/template/payments_sample.xlsx')} ><i
                            className="bi bi bi-cloud-arrow-down-fill fs-4"></i> Tải file mẫu
                        </Link>
                        <button className="col-auto btn btn-sm btn-primary mt-0 mx-1 mb-2" type="button"
                            onClick={() => setShowImport(true)}>
                            Nhập dòng tiền
                        </button> */}
                        {!isFullYear && (
                            <button className="col-auto btn btn-sm btn-primary mt-0 mx-1 mb-2" type="button"
                                onClick={handleExport}>
                                Tải xuống
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
        {search?.trashed_only !== 1 && (
            <div className="w-100 my-2">
                <label className='d-inline-block form-check form-check-sm form-check-custom form-check-solid mx-1'>
                    <input 
                        className='form-check-input' 
                        type='checkbox' 
                        name='include_trashed' 
                        checked={search?.include_trashed === 1}
                        onChange={toggleIncludeTrashed} 
                    />
                    <span className='form-check-label'>Hiển thị dữ liệu đã xóa</span>
                </label>
            </div>
        )}
      </div>
      <div className='card-body'>
        {search?.trashed_only === 1 ? (
            <>
                <div className="alert alert-danger" role="alert">
                    Các chứng từ đã xóa sẽ được lưu tại đây. Bạn có thể khôi phục hoạc xóa vĩnh viễn.
                </div>
                <CashBookAccountDeletedTable
                    data={data?.data || []}
                    selected={selected}
                    setSelected={setSelected}
                    openColConfig={() => setConfigModal({ ...configModal, show: true })}
                    header={header}
                    hideCols={hideCols}
                    handleSort={handleSort}
                />
            </>
        ) : (
            <CashBookAccountTable
            data={data?.data || []}
            selected={selected}
            setSelected={setSelected}
            openColConfig={() => setConfigModal({ ...configModal, show: true })}
            header={header}
            hideCols={hideCols}
            handleSort={handleSort}
            />
        )}
        
      </div>
      <div className='card-footer d-flex justify-content-between paginate-wrapper'>
        <PaginationLength selected={search.per_page || DEFAULT_PER_PAGE} onChange={handlePageLengthChange} />
        <Paginate
            pageCount={data?.meta?.last_page || 0}
            onPageChange={handlePageChange}
            forcePage={(search.page || 1) - 1}
        />
      </div>
      <ConfigModal
        {...configModal}
        header={header}
        hideCols={hideCols}
        handleChangeVisCols={handleChangeVisCols}
        onClose={() => { setConfigModal({ ...configModal, show: false }) }}
      />
       <ExcelToObjectImportModal
            show={showImport}
            handleClose={() => setShowImport(false)}
            onHide={() => setShowImport(false)}
            handleImport={handleImport}
            title={'Nhập dòng tiền'}
            submitText="Lưu"
        />
    </div>
  </>
}

export default CashBookAccount;